// src/components/Contato/Contato.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, PackageSearch, Rocket, Send, Sparkles } from "lucide-react";
import styles from "./css/Contato.module.css";
import saasContatoImg from "../../assets/images/saas-contato.png";
import { createLead } from "../../services/api";

const motivoOptions = [
    {
        value: "saas",
        title: "Criar um SaaS ou sistema",
        description: "Tenho uma ideia ou preciso de uma solução sob medida.",
        icon: Rocket,
    },
    {
        value: "produto",
        title: "Conhecer um produto HL",
        description: "Quero ver uma solução pronta e entender se atende meu negócio.",
        icon: PackageSearch,
    },
    {
        value: "outro",
        title: "Falar com a equipe",
        description: "Tenho uma dúvida, parceria ou outro assunto para tratar.",
        icon: MessageCircle,
    },
];

const necessidadeOptions = {
    saas: [
        "Criar sistema do zero",
        "Automatizar processos",
        "Melhorar sistema existente",
        "Validar uma ideia",
    ],
    produto: [
        "Agendar demonstração",
        "Comparar soluções",
        "Entender implantação",
        "Tirar dúvidas comerciais",
    ],
    outro: [
        "Solicitar orçamento",
        "Falar sobre parceria",
        "Pedir suporte",
        "Outro assunto",
    ],
};

const momentoOptions = [
    "Agora",
    "Nos próximos 30 dias",
    "Nos próximos meses",
    "Ainda estou pesquisando",
];

const initialForm = {
    nome: "",
    email: "",
    telefone: "",
    cidade: "",
    estado: "",
    necessidade: "",
    momento: "",
    mensagem: "",
    ideia: "",
    validou: "",
    possuiEmpresa: "",
    orcamento: "",
    produto: "",
    negocioFunciona: "",
    qtdClientes: "",
    mensagemOutro: "",
};

export default function Contato() {
    const [etapa, setEtapa] = useState(1);
    const [motivo, setMotivo] = useState("");
    const [form, setForm] = useState(initialForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState({ type: "", message: "" });
    const formRef = useRef(null);
    const contactFirstRef = useRef(null);

    const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const anim = useMemo(
        () => (prefersReducedMotion ? { duration: 0 } : { duration: 0.3 }),
        [prefersReducedMotion]
    );

    const getHeaderOffset = useCallback(() => {
        const root = document.documentElement;
        const v = getComputedStyle(root).getPropertyValue("--header-h").trim();
        const n = parseInt(v.replace("px", ""), 10);
        return (Number.isFinite(n) ? n : 72) + 8;
    }, []);

    const scrollFormIntoView = useCallback((behavior = "smooth") => {
        if (!formRef.current) return;
        const y =
            formRef.current.getBoundingClientRect().top +
            window.pageYOffset -
            getHeaderOffset();
        window.scrollTo({
            top: y,
            behavior: prefersReducedMotion ? "auto" : behavior,
        });
    }, [getHeaderOffset, prefersReducedMotion]);

    useEffect(() => {
        if (window.location.hash === "#contato") {
            setTimeout(() => scrollFormIntoView("auto"), 0);
        }
    }, [scrollFormIntoView]);

    useEffect(() => {
        if (etapa === 2) {
            scrollFormIntoView();
            setTimeout(() => contactFirstRef.current?.focus(), prefersReducedMotion ? 0 : 120);
        }
    }, [etapa, prefersReducedMotion, scrollFormIntoView]);

    const updateForm = (patch) => {
        if (feedback.message) setFeedback({ type: "", message: "" });
        setForm((current) => ({ ...current, ...patch }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "telefone") {
            const digits = value.replace(/\D/g, "").slice(0, 11);
            let masked = digits;
            if (digits.length <= 10) {
                masked = digits
                    .replace(/^(\d{0,2})/, "($1")
                    .replace(/^\((\d{2})\)?(\d{0,4})/, "($1) $2")
                    .replace(/(\d{4})(\d{1,4})$/, "$1-$2")
                    .replace(/\((\d{2})\) $/, "($1) ");
            } else {
                masked = digits
                    .replace(/^(\d{0,2})/, "($1")
                    .replace(/^\((\d{2})\)?(\d{0,5})/, "($1) $2")
                    .replace(/(\d{5})(\d{1,4})$/, "$1-$2")
                    .replace(/\((\d{2})\) $/, "($1) ");
            }
            updateForm({ telefone: masked });
            return;
        }

        if (name === "estado") {
            updateForm({ estado: value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 2) });
            return;
        }

        updateForm({ [name]: value });
    };

    const selectMotivo = (value) => {
        setMotivo(value);
        updateForm({ necessidade: "", mensagem: "" });
    };

    const podeAvancar = () =>
        motivo && form.necessidade.trim() && form.momento.trim();

    const podeEnviar = () =>
        form.nome.trim() && form.email.trim() && form.telefone.trim();

    const getMotivoExplicacao = () => {
        switch (motivo) {
            case "saas":
                return "Vamos entender o objetivo, o momento e o melhor próximo passo para transformar sua ideia em produto.";
            case "produto":
                return "Conte rapidamente o que precisa e nosso time indica o produto mais aderente antes de vender qualquer coisa.";
            case "outro":
                return "Use este caminho para dúvidas, parcerias ou assuntos que não cabem nas opções anteriores.";
            default:
                return "Escolha uma opção para adaptar as próximas perguntas ao seu momento.";
        }
    };

    const getMensagemPlaceholder = () => {
        switch (motivo) {
            case "saas":
                return "Ex: Quero criar uma plataforma para organizar clientes, pagamentos e atendimento.";
            case "produto":
                return "Ex: Quero entender se o GestorTx atende minha operação atual.";
            case "outro":
                return "Ex: Gostaria de conversar sobre uma parceria.";
            default:
                return "Conte em poucas palavras o que precisamos saber antes do contato.";
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isSubmitting || !podeEnviar()) return;

        const payload = {
            ...form,
            motivo,
            ideia: motivo === "saas" ? form.mensagem : "",
            produto: motivo === "produto" ? form.necessidade : "",
            mensagemOutro: motivo === "outro" ? form.mensagem : "",
        };

        setIsSubmitting(true);
        setFeedback({ type: "", message: "" });

        createLead(payload)
            .then(() => {
                setFeedback({
                    type: "success",
                    message: "Recebemos seu contato. Em breve nosso time vai chamar você pelo WhatsApp.",
                });
                setForm(initialForm);
                setMotivo("");
                setEtapa(1);
                setTimeout(() => scrollFormIntoView(), 0);
            })
            .catch((error) => {
                setFeedback({
                    type: "error",
                    message: error.message || "Nao foi possivel enviar. Tente novamente.",
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const currentNeedOptions = necessidadeOptions[motivo] || [];

    return (
        <section id="contato" className={styles.contatoSection} aria-labelledby="contato-titulo">
            <div className={styles.container}>
                <div className={styles.blocoContatoAtrativo}>
                    <div className={styles.texto}>
                        <h2 id="contato-titulo">Seu próximo passo começa com uma conversa curta</h2>
                        <p>
                            Conte o que você quer resolver e em que momento está. A HL Software responde com um
                            caminho claro, sem briefing cansativo e sem compromisso.
                        </p>
                        <ul>
                            <li>Atendimento direto pelo WhatsApp</li>
                            <li>Entendimento antes de orçamento</li>
                            <li>Soluções sob medida ou produtos prontos</li>
                        </ul>
                    </div>
                    <div className={styles.imagem}>
                        <img src={saasContatoImg} alt="Mockup de dashboard SaaS em notebook" />
                    </div>
                </div>

                <form ref={formRef} className={styles.form} onSubmit={handleSubmit} noValidate>
                    <div className={styles.formIntro}>
                        <span>
                            <Sparkles size={16} aria-hidden="true" />
                            Leva menos de 1 minuto
                        </span>
                        <h3>Como podemos ajudar?</h3>
                        <p>Responda só o essencial. O resto conversamos pelo WhatsApp.</p>
                    </div>

                    <div className={styles.stepper} role="tablist" aria-label="Etapas do formulário">
                        <div className={`${styles.step} ${etapa === 1 ? styles.active : ""}`} role="tab" aria-selected={etapa === 1}>
                            <div className={styles.stepCircle}>1</div>
                            <span>Objetivo</span>
                        </div>
                        <div className={`${styles.step} ${etapa === 2 ? styles.active : ""}`} role="tab" aria-selected={etapa === 2}>
                            <div className={styles.stepCircle}>2</div>
                            <span>Contato</span>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {etapa === 1 && (
                            <motion.div
                                key="etapa1"
                                className={styles.formStep}
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 30 }}
                                transition={anim}
                            >
                                <fieldset className={styles.fieldset}>
                                    <legend>O que você procura?</legend>
                                    <div className={styles.optionCards}>
                                        {motivoOptions.map((option) => {
                                            const Icon = option.icon;
                                            const isSelected = motivo === option.value;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={`${styles.optionCard} ${isSelected ? styles.optionCardActive : ""}`}
                                                    onClick={() => selectMotivo(option.value)}
                                                    aria-pressed={isSelected}
                                                >
                                                    <span className={styles.optionIcon}>
                                                        <Icon size={20} aria-hidden="true" />
                                                    </span>
                                                    <strong>{option.title}</strong>
                                                    <small>{option.description}</small>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                <div className={styles.inlineHint}>
                                    <strong>Como vamos usar isso?</strong>
                                    <span>{getMotivoExplicacao()}</span>
                                </div>

                                {motivo && (
                                    <>
                                        <fieldset className={styles.fieldset}>
                                            <legend>Qual necessidade combina mais?</legend>
                                            <div className={styles.chips}>
                                                {currentNeedOptions.map((option) => (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        className={`${styles.chip} ${form.necessidade === option ? styles.chipActive : ""}`}
                                                        onClick={() => updateForm({ necessidade: option })}
                                                        aria-pressed={form.necessidade === option}
                                                    >
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                            <label className={styles.compactLabel} htmlFor="necessidade">
                                                Se preferir, escreva do seu jeito
                                            </label>
                                            <input
                                                id="necessidade"
                                                name="necessidade"
                                                value={form.necessidade}
                                                onChange={handleChange}
                                                placeholder="Ex: preciso organizar clientes e cobranças"
                                            />
                                        </fieldset>

                                        <fieldset className={styles.fieldset}>
                                            <legend>Em que momento você está?</legend>
                                            <div className={styles.chips}>
                                                {momentoOptions.map((option) => (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        className={`${styles.chip} ${form.momento === option ? styles.chipActive : ""}`}
                                                        onClick={() => updateForm({ momento: option })}
                                                        aria-pressed={form.momento === option}
                                                    >
                                                        {option}
                                                    </button>
                                                ))}
                                            </div>
                                        </fieldset>
                                    </>
                                )}

                                <button
                                    type="button"
                                    className={styles.nextButton}
                                    disabled={!podeAvancar()}
                                    onClick={() => setEtapa(2)}
                                >
                                    Continuar
                                </button>
                            </motion.div>
                        )}

                        {etapa === 2 && (
                            <motion.div
                                key="etapa2"
                                className={styles.formStep}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={anim}
                            >
                                <div className={styles.selectedSummary}>
                                    <strong>{form.necessidade}</strong>
                                    <span>{form.momento}</span>
                                </div>

                                <label>Nome completo*</label>
                                <input ref={contactFirstRef} name="nome" value={form.nome} onChange={handleChange} required />

                                <label>E-mail*</label>
                                <input type="email" name="email" value={form.email} onChange={handleChange} required />

                                <label>WhatsApp ou telefone*</label>
                                <input
                                    name="telefone"
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    value={form.telefone}
                                    onChange={handleChange}
                                    required
                                    inputMode="tel"
                                />

                                <div className={styles.ladoALado}>
                                    <div>
                                        <label>Cidade</label>
                                        <input name="cidade" value={form.cidade} onChange={handleChange} />
                                    </div>
                                    <div>
                                        <label>Estado</label>
                                        <input
                                            name="estado"
                                            maxLength={2}
                                            style={{ textTransform: "uppercase" }}
                                            value={form.estado}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <label>Quer acrescentar algo? <span className={styles.optionalText}>Opcional</span></label>
                                <textarea
                                    name="mensagem"
                                    value={form.mensagem}
                                    onChange={handleChange}
                                    placeholder={getMensagemPlaceholder()}
                                />

                                <p className={styles.trustNote}>
                                    Resposta sem compromisso. Vamos entender seu caso antes de falar em orçamento.
                                </p>

                                <div className={styles.botoesEtapa}>
                                    <button
                                        type="button"
                                        className={styles.backButton}
                                        onClick={() => {
                                            setEtapa(1);
                                            setTimeout(() => scrollFormIntoView(), 0);
                                        }}
                                    >
                                        Voltar
                                    </button>
                                    <button type="submit" className={styles.submitButton} disabled={isSubmitting || !podeEnviar()}>
                                        {isSubmitting ? (
                                            "Enviando..."
                                        ) : (
                                            <>
                                                <Send size={17} aria-hidden="true" />
                                                Receber contato pelo WhatsApp
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {feedback.message && (
                        <p
                            className={`${styles.feedback} ${
                                feedback.type === "success" ? styles.feedbackSuccess : styles.feedbackError
                            }`}
                            role={feedback.type === "error" ? "alert" : "status"}
                        >
                            {feedback.message}
                        </p>
                    )}

                    <p className={styles.rodapeMensagem}>
                        Sem spam. O contato vai direto para nosso CRM para respondermos com contexto.
                    </p>
                </form>
            </div>
        </section>
    );
}
